import { MAX_PLAYERS } from '../../core/players';

export type LevelRotation = 'random' | 'ordered';
export type BotDifficulty = 'beginner' | 'standard' | 'expert';
export const MATCH_MODES = [
  'classic',
  'teams',
  'king-of-hill',
  'weapon-roulette',
  'shared-stock',
  'arms-race',
  'office-brawl',
] as const;
export type MatchMode = (typeof MATCH_MODES)[number];
export const MUTATORS = ['low-gravity', 'giant-knockback', 'melee-only', 'same-weapon'] as const;
export type Mutator = (typeof MUTATORS)[number];
export const BOT_STYLES = ['aggressive', 'defensive', 'opportunistic'] as const;
export type BotStyle = (typeof BOT_STYLES)[number];
export const BOT_SKILL: Record<BotDifficulty, number> = {
  beginner: 0.18,
  standard: 0.45,
  expert: 0.95,
};

export interface MatchSettings {
  mode: MatchMode;
  mutators: Mutator[];
  teams: number[];
  botStyles: BotStyle[];
  modeScoreLimit: number;
  teamStocks: number;
  rouletteSeconds: number;
  maxHp: number;
  firstTo: number;
  showWins: boolean;
  rotation: LevelRotation;
  enabledWeapons: string[] | 'all';
  enabledLevels: string[] | 'all';
  playerCount: number;
  bots: number;
  botDifficulty: BotDifficulty;
  /** Seconds of fighting before the arena closes in; 0 disables sudden death. */
  roundTimeLimit: number;
  /** Palette index per slot (humans pick theirs on the join screen); slots past the end use their index. */
  colors?: number[];
}

export const DEFAULT_SETTINGS: MatchSettings = {
  mode: 'classic',
  mutators: [],
  teams: [],
  botStyles: [],
  modeScoreLimit: 20,
  teamStocks: 4,
  rouletteSeconds: 12,
  maxHp: 100,
  firstTo: 0,
  showWins: true,
  rotation: 'random',
  enabledWeapons: 'all',
  enabledLevels: 'all',
  playerCount: 2,
  bots: 0,
  botDifficulty: 'standard',
  roundTimeLimit: 60,
};

export function mergeSettings(partial?: Partial<MatchSettings>): MatchSettings {
  const settings = { ...DEFAULT_SETTINGS, ...partial };
  const bounded = (value: number, fallback: number, min: number, max: number) =>
    Number.isFinite(value) ? Math.max(min, Math.min(max, Math.round(value))) : fallback;
  settings.playerCount = bounded(
    settings.playerCount,
    DEFAULT_SETTINGS.playerCount,
    0,
    MAX_PLAYERS,
  );
  settings.bots = bounded(settings.bots, 0, 0, MAX_PLAYERS - settings.playerCount);
  settings.mode = MATCH_MODES.includes(settings.mode) ? settings.mode : 'classic';
  settings.mutators = [
    ...new Set(
      Array.isArray(settings.mutators) ? settings.mutators.filter((m) => MUTATORS.includes(m)) : [],
    ),
  ];
  if (settings.mode === 'arms-race' || settings.mode === 'office-brawl') {
    settings.mutators = settings.mutators.filter(
      (mutator) => mutator !== 'melee-only' && mutator !== 'same-weapon',
    );
  }
  settings.teams = Array.isArray(settings.teams)
    ? settings.teams
        .slice(0, MAX_PLAYERS)
        .map((team, slot) => (team === 0 || team === 1 ? team : slot % 2))
    : [];
  if (settings.mode === 'teams' || settings.mode === 'shared-stock') {
    // Only seats that will spawn matter. Missing assignments use the same alternating
    // default as teamForSlot; a team on an unused seat cannot prevent an empty opposition.
    const count = Math.max(1, settings.playerCount + settings.bots);
    const activeTeams = Array.from(
      { length: count },
      (_, slot) => settings.teams[slot] ?? slot % 2,
    );
    if (count > 1 && new Set(activeTeams).size === 1) {
      settings.teams = Array.from({ length: count }, (_, slot) => slot % 2);
    }
  }
  settings.botStyles = Array.isArray(settings.botStyles)
    ? settings.botStyles
        .slice(0, MAX_PLAYERS)
        .map((style, slot) => (BOT_STYLES.includes(style) ? style : BOT_STYLES[slot % 3]!))
    : [];
  settings.colors = Array.isArray(settings.colors)
    ? settings.colors
        .slice(0, MAX_PLAYERS)
        .map((color, slot) => bounded(color, slot, 0, MAX_PLAYERS - 1))
    : undefined;
  settings.modeScoreLimit = bounded(settings.modeScoreLimit, 20, 5, 120);
  settings.teamStocks = bounded(settings.teamStocks, 4, 1, 12);
  settings.rouletteSeconds = bounded(settings.rouletteSeconds, 12, 3, 60);
  settings.botDifficulty =
    settings.botDifficulty in BOT_SKILL ? settings.botDifficulty : 'standard';
  settings.roundTimeLimit = Number.isFinite(settings.roundTimeLimit)
    ? Math.max(0, Math.min(300, settings.roundTimeLimit))
    : 60;
  return settings;
}
